import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { register, getCaptcha } from '@/api/auth'
import { getAppConfig } from '@/api/config'
import toast from 'react-hot-toast'
import logoLarge from '@/assets/logo-large.png'
import { COUNTRIES, DEFAULT_COUNTRY } from '@/data/countries'

export default function RegisterPage() {
  const navigate = useNavigate()
  const { isAuthenticated, refreshUser } = useAuth()
  const [searchParams] = useSearchParams()

  const isInvitation = searchParams.get('invitation') === 'true'
  const invitedEmail = searchParams.get('email') || ''
  const invitationToken = searchParams.get('token') || ''

  const [email, setEmail] = useState(invitedEmail)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [organization, setOrganization] = useState('')
  // The country decides the checkout currency (IN -> INR, else USD) and the
  // tax rule on every invoice, so it is collected once here rather than
  // interrupting the user mid-purchase later. Pre-filled from the browser
  // locale, which they can always change.
  const [country, setCountry] = useState(DEFAULT_COUNTRY)
  const [captchaInput, setCaptchaInput] = useState('')
  const [captchaValue, setCaptchaValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  // null = still resolving. Self-service sign-up is closed while the instance
  // is in beta; invitation-based registration is always allowed.
  const [signupEnabled, setSignupEnabled] = useState<boolean | null>(null)

  // Generate username from email
  const autoUsername = useMemo(() => {
    if (!email) return ''
    const localPart = email.split('@')[0]
    return localPart.replace(/[^a-zA-Z0-9._-]/g, '').toLowerCase()
  }, [email])

  useEffect(() => {
    if (!username || username === autoUsername) {
      setUsername(autoUsername)
    }
  }, [autoUsername, username])

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/workbench', { replace: true })
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    getAppConfig()
      .then((cfg) => setSignupEnabled(!!cfg.signup_enabled))
      .catch(() => setSignupEnabled(false))
  }, [])

  // Load captcha
  useEffect(() => {
    loadCaptcha()
  }, [])

  const loadCaptcha = async () => {
    try {
      const data = await getCaptcha()
      setCaptchaValue(data.captcha)
    } catch {
      // Captcha loading is optional
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !password || !confirmPassword) {
      toast.error('Please fill in all required fields')
      return
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    if (!country) {
      toast.error('Please select your country')
      return
    }
    if (!isInvitation && !organization) {
      toast.error('Organization is required')
      return
    }
    if (captchaValue && captchaInput.toUpperCase() !== captchaValue.toUpperCase()) {
      toast.error('Invalid captcha. Please try again.')
      loadCaptcha()
      setCaptchaInput('')
      return
    }

    setLoading(true)
    try {
      const result = await register({
        email,
        username: username || autoUsername,
        password,
        confirm_password: confirmPassword,
        organization: isInvitation ? undefined : organization,
        country: country || undefined,
        invitation_token: isInvitation ? invitationToken : undefined,
      })

      // An invitation into an organization that requires two-factor sign-in
      // from the first sign-in: the login page runs the setup.
      if (result.mfa_required || result.mfa_enrolment_required) {
        toast.success('Account created — sign in to set up two-factor sign-in.')
        navigate('/login', { replace: true })
        return
      }
      if (result.redirect) {
        // Invitation-based: auto-logged-in
        await refreshUser()
        navigate(result.redirect, { replace: true })
        return
      }

      if (result.needs_activation) {
        setSuccess(true)
        if (result.email_sent === false) {
          toast.error(result.message || 'Account created but failed to send verification email. Please try resending.')
        } else {
          toast.success('Account created! Check your email for the activation link.')
        }
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Registration failed'
      toast.error(msg)
      loadCaptcha()
      setCaptchaInput('')
    } finally {
      setLoading(false)
    }
  }

  // Beta: no self-service accounts — point people at the waitlist. (An
  // invitation link still registers normally.)
  if (!isInvitation && signupEnabled === false) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--color-bg)',
        padding: '2rem',
      }}>
        <div style={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
          <img src={logoLarge} alt="twasta.ai" style={{ height: 44, marginBottom: '1.5rem' }} />
          <div style={{
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '2rem',
          }}>
            <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.625rem' }}>
              We're in beta
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginBottom: '1.5rem' }}>
              Account creation isn't open yet. Join the waitlist and we'll email you an
              activation link as soon as your access is approved.
            </p>
            <Link to="/waitlist" className="btn btn-primary btn-lg w-full">
              Join the waitlist
            </Link>
            <div style={{ marginTop: '1rem', fontSize: '0.9375rem' }}>
              <Link to="/login">Back to sign in</Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--color-bg)',
        padding: '2rem',
      }}>
        <div style={{
          maxWidth: 460,
          width: '100%',
          textAlign: 'center',
          animation: 'slideUp 0.3s ease-out',
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--color-accent-weak)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
          }}>
            <span className="material-icons" style={{ fontSize: 33, color: 'var(--color-accent)' }}>
              mark_email_read
            </span>
          </div>
          <h2 style={{ fontSize: '1.5625rem', fontWeight: 600, marginBottom: '0.75rem' }}>
            Check Your Email
          </h2>
          <p style={{
            color: 'var(--color-text-secondary)',
            fontSize: '1rem',
            lineHeight: 1.7,
            marginBottom: '2rem',
          }}>
            We've sent an activation link to <strong style={{ color: 'var(--color-text)' }}>{email}</strong>.
            Click the link in the email to activate your account.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <Link to="/activate" className="btn btn-secondary">
              Enter Code Manually
            </Link>
            <Link to="/" className="btn btn-primary">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--color-bg)',
      padding: '2rem',
    }}>
      <div style={{
        maxWidth: 460,
        width: '100%',
        animation: 'slideUp 0.3s ease-out',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img
            src={logoLarge}
            alt="Twasta.ai"
            style={{ height: 40, objectFit: 'contain' }}
          />
          <h2 style={{
            fontSize: '1.3125rem',
            fontWeight: 600,
            marginTop: '0.75rem',
          }}>
            {isInvitation ? 'Accept Invitation' : 'Create Your Account'}
          </h2>
          <p style={{
            fontSize: '0.9375rem',
            color: 'var(--color-text-secondary)',
            marginTop: '0.375rem',
          }}>
            {isInvitation
              ? 'Set up your credentials to join the team'
              : 'Start building applications with AI'}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.75rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.125rem',
        }}>
          {/* Email */}
          <div>
            <label htmlFor="reg-email">Email</label>
            <input
              id="reg-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              disabled={isInvitation && !!invitedEmail}
            />
          </div>

          {/* Username */}
          <div>
            <label htmlFor="reg-username">Username</label>
            <input
              id="reg-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Auto-generated from email"
            />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
              Auto-generated from your email. You can customize it.
            </span>
          </div>

          {/* Password */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label htmlFor="reg-password">Password</label>
              <input
                id="reg-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <label htmlFor="reg-confirm">Confirm Password</label>
              <input
                id="reg-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                autoComplete="new-password"
                required
              />
            </div>
          </div>

          {/* Organization - hidden for invitations */}
          {!isInvitation && (
            <div>
              <label htmlFor="reg-org">Organization</label>
              <input
                id="reg-org"
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                placeholder="Your company or team name"
                required
              />
            </div>
          )}

          {/* Country — asked once, at sign-up, so a later purchase never has to
              stop and ask. It is what picks the billing currency and tax rule. */}
          <div>
            <label htmlFor="reg-country">Country</label>
            <select
              id="reg-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              style={{ width: '100%' }}
            >
              <option value="">Select your country</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
              Used to determine your billing currency.
            </div>
          </div>

          {/* CAPTCHA */}
          {captchaValue && (
            <div>
              <label>Verify you're human</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '0.5rem 1rem',
                  fontFamily: 'monospace',
                  fontSize: '1.3125rem',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  color: 'var(--color-accent)',
                  userSelect: 'none',
                  minWidth: 120,
                  textAlign: 'center',
                }}>
                  {captchaValue}
                </div>
                <button
                  type="button"
                  onClick={loadCaptcha}
                  className="btn btn-ghost btn-icon"
                  title="Refresh captcha"
                >
                  <span className="material-icons">refresh</span>
                </button>
                <input
                  type="text"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  placeholder="Enter code"
                  style={{ maxWidth: 140 }}
                  required
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-lg w-full"
            disabled={loading}
            style={{ marginTop: '0.25rem' }}
          >
            {loading ? (
              <>
                <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                Creating account...
              </>
            ) : isInvitation ? (
              'Accept Invitation'
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div style={{
          textAlign: 'center',
          marginTop: '1.25rem',
          fontSize: '0.9375rem',
          color: 'var(--color-text-secondary)',
        }}>
          Already have an account?{' '}
          <Link to="/">Sign in</Link>
        </div>
      </div>
    </div>
  )
}
